import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from '@emotion/styled';
import { AGENT_KIND_INFO, AGENT_KIND_DISCLAIMER } from './agentKind';
import MessageList from './components/MessageList';
import MessageInput from './components/MessageInput';
import useRAGQuery from './hooks/useRAGQuery';
import { useTextReveal } from './hooks/useTextReveal';
import { getUserId } from './utils/getUserId';
import API_ENDPOINTS from '../config/api';
import ModalWrapper, { ModalCard } from '../components/ModalWrapper';
import { useTranslation } from '../hooks/useTranslation';
import AgentAvatar from './components/AgentAvatar';
import ReportPanel from './components/ReportPanel';

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: #0a0a0a;
`;

const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
  flex-grow: 0;
`;

// The model and agent selectors share one pill: same border, fill, size and chevron.
const CHEVRON = "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path d='M1 1l4 4 4-4' fill='none' stroke='rgba(255,255,255,0.75)' stroke-width='1.5'/></svg>\")";
const ModelPill = styled.label`
  position: relative;
  display: inline-flex;
  align-items: center;
  & > span:first-of-type { position: absolute; left: 4px; pointer-events: none; }
`;
const ModelSelector = styled.select`
  appearance: none;
  -webkit-appearance: none;
  height: 30px;
  padding: 0 26px 0 34px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.04) ${CHEVRON} no-repeat right 10px center;
  color: rgba(255, 255, 255, 0.85);
  font-size: 12px;
  cursor: pointer;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;

  &:hover { background-color: rgba(255, 255, 255, 0.08); }
  option { background: #1a1a2e; color: white; }
`;

const ClearButton = styled.button`
  width: 26px;
  height: 26px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(239, 68, 68, 0.15);
    border-color: rgba(239, 68, 68, 0.3);
    color: #ef4444;
  }
`;

const AgentMenu = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const AgentSelectButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 30px;
  padding: 0 10px 0 4px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.85);
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
  &:hover { background: rgba(255, 255, 255, 0.08); }
`;

const AvatarStackSmall = styled.span`
  display: inline-flex;
  & > * + * { margin-left: -8px; }
`;

const AgentPopover = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 20;
  min-width: 300px;
  max-width: min(90vw, 420px);
  background: #141414;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  padding: 6px;
`;

const AgentOption = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  opacity: ${(p) => (p.included ? 1 : 0.5)};
  &:hover { background: rgba(255, 255, 255, 0.06); }
  input { accent-color: #4caf50; }
`;

const AgentOptionName = styled.span`
  flex: 1;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
  small { color: rgba(255, 255, 255, 0.45); margin-left: 6px; }
`;

const InfoButton = styled.button`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  &:hover { background: rgba(255, 255, 255, 0.1); color: #fff; }
`;

const AgentInfoCard = styled.div`
  padding: 20px 24px;
  max-width: 480px;
  color: rgba(255, 255, 255, 0.9);
  h3 { margin: 0; font-size: 16px; }
  p { font-size: 13px; line-height: 1.5; color: rgba(255, 255, 255, 0.75); }
  dl { font-size: 12px; color: rgba(255, 255, 255, 0.55); margin: 10px 0 0; }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const ConfirmCard = styled.div`
  background: #1a1a2e;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  padding: 20px 24px;
  max-width: 280px;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
`;

const ConfirmText = styled.p`
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
  line-height: 1.5;
  margin: 0 0 16px 0;
`;

const ConfirmButtons = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
`;

const ConfirmBtn = styled.button`
  padding: 7px 18px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid;
`;

const CancelBtn = styled(ConfirmBtn)`
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.7);
  &:hover { background: rgba(255, 255, 255, 0.15); color: white; }
`;

const DeleteBtn = styled(ConfirmBtn)`
  background: rgba(239, 68, 68, 0.15);
  border-color: rgba(239, 68, 68, 0.3);
  color: #ef4444;
  &:hover { background: rgba(239, 68, 68, 0.25); }
`;

/**
 * ChatPanel — Headless chat body (messages + input + streaming logic).
 * Can be embedded in any container: drawer panel, modal, or standalone window.
 *
 * Accepts contextRef (a React ref) so the parent can update context
 * (screen, solution) without remounting this component.
 */
const ChatPanel = ({ contextRef, deviceCode }) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [userId] = useState(() => getUserId(deviceCode));
  // Threads of agent work (a bounce, …): their results and reports, by id; the one open beside the chat
  const [threads, setThreads] = useState({});
  const [openThread, setOpenThread] = useState(null);
  const [model, setModel] = useState('sonnet');
  const [headaiAvailable, setHeadaiAvailable] = useState(false);
  // Kuutio-agents (the guest list): all in by default; leaving one out is remembered per viewer
  const [agentRoster, setAgentRoster] = useState([]);
  const [excludedAgents, setExcludedAgents] = useState(() => new Set());
  const rosterInstanceId = contextRef.current?.instanceId || null;
  const excludedKey = `kuutio-chat-agents-excluded-${rosterInstanceId || 'none'}`;
  const [agentListOpen, setAgentListOpen] = useState(false);
  const [agentInfo, setAgentInfo] = useState(null);
  const lastUserQuestion = useRef('');
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const { streamQuery, loadHistory, clearConversation, sendReaction, isLoading } = useRAGQuery();
  const { revealedText, pushText, finish: finishReveal, reset: resetReveal } = useTextReveal(3);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const messageIdCounter = useRef(0);
  const streamingMsgId = useRef(null);

  // Load conversation history on mount
  useEffect(() => {
    const load = async () => {
      const { messages: history, threads: savedThreads } = await loadHistory(userId, contextRef.current?.instanceId);
      if (savedThreads) setThreads(savedThreads);
      if (history.length > 0) {
        setMessages(withSpeakers(history.map((m) => (m.type === 'kuutio_agent' ? { ...m, dbId: m.id } : m))));
        messageIdCounter.current = history.length;
      }
      setHistoryLoaded(true);
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    fetch(API_ENDPOINTS.RAG_CHAT_PROVIDERS())
      .then((r) => r.json())
      .then((data) => setHeadaiAvailable(Boolean(data?.headai)))
      .catch(() => setHeadaiAvailable(false));
  }, []);

  useEffect(() => {
    if (!rosterInstanceId) return;
    let cancelled = false;
    fetch(API_ENDPOINTS.RAG_CHAT_AGENTS(rosterInstanceId))
      .then((r) => (r.ok ? r.json() : { agents: [] }))
      .then((d) => { if (!cancelled) setAgentRoster(d.agents || []); })
      .catch(() => { if (!cancelled) setAgentRoster([]); });
    try {
      const saved = JSON.parse(localStorage.getItem(excludedKey) || '[]');
      setExcludedAgents(new Set(Array.isArray(saved) ? saved : []));
    } catch { setExcludedAgents(new Set()); }
    return () => { cancelled = true; };
  }, [rosterInstanceId, excludedKey]);

  const toggleAgent = useCallback((slug) => {
    setExcludedAgents((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug); else next.add(slug);
      try { localStorage.setItem(excludedKey, JSON.stringify([...next])); } catch { /* per-viewer convenience only */ }
      return next;
    });
  }, [excludedKey]);

  const handleClearConversation = useCallback(async () => {
    await clearConversation(userId, contextRef.current?.instanceId);
    setThreads({});
    setOpenThread(null);
    setMessages([]);
    messageIdCounter.current = 0;
    setShowClearConfirm(false);
    // Tell ChatDrawerTab the chat is empty again so the onboarding bubble
    // can come back on the next drawer close.
    try { window.dispatchEvent(new CustomEvent('kuutio:chat-cleared')); } catch { /* ignore */ }
  }, [clearConversation, userId, contextRef]);

  // Reactions v0: accept / reject / fix on an agent's message, saved by message id
  const handleReact = useCallback(async (message, reaction, reason) => {
    if (!message.dbId) return;
    const saved = await sendReaction({ messageId: message.dbId, userId, reaction, reason });
    if (!saved) return;
    setMessages(prev => prev.map(m => (m.id === message.id ? { ...m, reaction: { reaction: saved.reaction, reason: saved.reason } } : m)));
  }, [sendReaction, userId]);

  const handleSendMessage = useCallback(async (userMessage) => {
    if (userMessage.trim().toLowerCase() === '/clear') {
      await handleClearConversation();
      return;
    }

    lastUserQuestion.current = userMessage;
    const userMsgId = `msg-${userId}-${++messageIdCounter.current}`;
    setMessages(prev => [...prev, { id: userMsgId, type: 'user', text: userMessage, timestamp: new Date() }]);

    const agentMsgId = `msg-${userId}-${++messageIdCounter.current}`;
    streamingMsgId.current = agentMsgId;
    resetReveal();
    setMessages(prev => [...prev, {
      id: agentMsgId, type: 'agent', text: '', timestamp: new Date(), streaming: true, toolActivity: null,
    }]);

    try {
      // Read context at send time — always gets the latest screen/solution
      const context = {
        ...contextRef.current,
        agents: agentRoster.filter((a) => !excludedAgents.has(a.slug)).map((a) => a.slug),
      };
      await streamQuery(userMessage, context, userId, model, ({ event, data }) => {
        switch (event) {
          case 'text_delta':
            pushText(data.text);
            break;
          case 'tool_use':
            setMessages(prev => prev.map(m =>
              m.id === agentMsgId ? { ...m, toolActivity: toolLabel(data.tool, t) } : m
            ));
            break;
          case 'tool_result':
            setMessages(prev => prev.map(m =>
              m.id === agentMsgId ? { ...m, toolActivity: null } : m
            ));
            break;
          case 'sources':
            setMessages(prev => prev.map(m =>
              m.id === agentMsgId ? { ...m, sources: data } : m
            ));
            break;
          case 'thread_report':
            // A thread's filter and report are written: fold the turns, show the button
            setThreads(prev => ({ ...prev, [data.id]: data }));
            break;
          case 'agent_offer':
            setMessages(prev => prev.map(m =>
              m.id === agentMsgId ? { ...m, offers: [...(m.offers || []), data] } : m
            ));
            break;
          case 'agent_answer': {
            // A Kuutio-agent speaks as itself: its own message, before the assistant's
            const speakerMsg = {
              id: `msg-${userId}-${++messageIdCounter.current}`, type: 'kuutio_agent',
              speaker: { ...data.speaker, thickness: data.thickness }, text: data.text, sources: data.sources, timestamp: new Date(),
            };
            setMessages(prev => {
              const at = prev.findIndex(m => m.id === agentMsgId);
              const next = [...prev];
              next.splice(at < 0 ? prev.length : at, 0, speakerMsg);
              return next.map(m => m.id === agentMsgId ? { ...m, speakers: [...(m.speakers || []), speakerMsg.speaker] } : m);
            });
            break;
          }
          case 'done': {
            const fullText = finishReveal();
            // The agents' messages of this turn were just saved; their ids arrive in order
            const ids = Array.isArray(data?.agentMessageIds) ? [...data.agentMessageIds] : [];
            setMessages(prev => {
              const from = prev.findIndex(m => m.id === userMsgId);
              return prev.map((m, i) => {
                if (m.id === agentMsgId) return { ...m, text: fullText, streaming: false, toolActivity: null };
                if (i > from && m.type === 'kuutio_agent' && !m.dbId && ids.length) return { ...m, dbId: ids.shift() };
                return m;
              });
            });
            streamingMsgId.current = null;
            break;
          }
          case 'error':
            finishReveal();
            streamingMsgId.current = null;
            setMessages(prev => prev.map(m =>
              m.id === agentMsgId
                ? { ...m, text: m.text || t('chatAgent.errorPrefix', { message: data.message }), streaming: false, toolActivity: null }
                : m
            ));
            break;
        }
      });
    } catch {
      finishReveal();
      streamingMsgId.current = null;
      setMessages(prev => prev.map(m =>
        m.id === agentMsgId
          ? { ...m, text: m.text || t('chatAgent.connectionError'), streaming: false, toolActivity: null }
          : m
      ));
    }
  }, [contextRef, userId, model, streamQuery, handleClearConversation, pushText, finishReveal, resetReveal, t, agentRoster, excludedAgents]);

  // Sync animated text into the streaming message
  useEffect(() => {
    const msgId = streamingMsgId.current;
    if (!msgId) return;
    setMessages(prev => prev.map(m =>
      m.id === msgId ? { ...m, text: revealedText } : m
    ));
  }, [revealedText]);

  return (
    <PanelContainer>
      <ChatHeader>
        <ModelPill title={t('chatAgent.modelTitle')}>
          <AgentAvatar speaker={model === 'headai' ? { slug: 'H', name: 'HeadAI' } : { slug: 'A', name: 'Anthropic' }} size={22} />
          <ModelSelector value={model} onChange={(e) => setModel(e.target.value)} aria-label={t('chatAgent.modelTitle')}>
            <option value="sonnet">Sonnet 5.5</option>
            <option value="opus">Opus 5.5</option>
            <option value="fable">Fable 5.1</option>
            {headaiAvailable && <option value="headai">HeadAI</option>}
          </ModelSelector>
        </ModelPill>
        <HeaderRight>
        {agentRoster.length > 0 && (
          <AgentMenu>
            <AgentSelectButton type="button" onClick={() => setAgentListOpen((v) => !v)} title={t('chatAgent.agentsSelectTitle')}>
              <AvatarStackSmall>
                {agentRoster.filter((a) => !excludedAgents.has(a.slug)).slice(0, 4).map((a) => <AgentAvatar key={a.slug} speaker={a} size={22} />)}
              </AvatarStackSmall>
              {t('chatAgent.agentsSelectLabel', { included: agentRoster.length - [...excludedAgents].filter((x) => agentRoster.some((a) => a.slug === x)).length, total: agentRoster.length })}
              <span aria-hidden>▾</span>
            </AgentSelectButton>
            {agentListOpen && (
              <AgentPopover>
                {agentRoster.map((a) => {
                  const included = !excludedAgents.has(a.slug);
                  return (
                    <AgentOption key={a.slug} included={included}>
                      <input type="checkbox" checked={included} onChange={() => toggleAgent(a.slug)} />
                      <AgentAvatar speaker={a} size={24} />
                      <AgentOptionName>{a.name}<small>{a.slug.toUpperCase()}</small></AgentOptionName>
                      <InfoButton type="button" aria-label={t('chatAgent.agentInfoTitle')} onClick={(e) => { e.preventDefault(); setAgentInfo(a); }}>i</InfoButton>
                    </AgentOption>
                  );
                })}
              </AgentPopover>
            )}
          </AgentMenu>
        )}
          {messages.length > 0 && (
            <ClearButton onClick={() => setShowClearConfirm(true)} title={t('chatAgent.clearTitle')}>
              &#x27F3;
            </ClearButton>
          )}
        </HeaderRight>
      </ChatHeader>
      {agentInfo && (
        <ModalWrapper visible={!!agentInfo} onClose={() => setAgentInfo(null)}>
          <ModalCard>
            <AgentInfoCard>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <AgentAvatar speaker={agentInfo} size={44} />
                <div>
                  <h3>{agentInfo.name}</h3>
                  <small style={{ color: 'rgba(255,255,255,0.5)' }}>{agentInfo.slug.toUpperCase()} · {t(AGENT_KIND_INFO[agentInfo.kind] || 'chatAgent.agentInfoSynthesis')}{agentInfo.thickness ? ` · ${t(agentInfo.thickness === 'thin' ? 'chatAgent.agentThin' : 'chatAgent.agentThick')}` : ''}</small>
                </div>
              </div>
              {agentInfo.topic && <p><strong>{t('chatAgent.agentInfoTopic')}:</strong> {agentInfo.topic}</p>}
              <p>{agentInfo.perspective || t('chatAgent.agentInfoNoPerspective')}</p>
              <dl>{t(AGENT_KIND_DISCLAIMER[agentInfo.kind] || 'chatAgent.agentInfoDisclaimer')}</dl>
            </AgentInfoCard>
          </ModalCard>
        </ModalWrapper>
      )}
      <MessageList
        messages={messages}
        isLoading={isLoading && historyLoaded}
        onAskAgent={(speaker) => handleSendMessage(t('chatAgent.askAgentPrefix', { name: speaker.slug.toUpperCase() }) + ' ' + lastUserQuestion.current)}
        onDeepDive={() => handleSendMessage(t('chatAgent.deepDivePrefix') + ' ' + lastUserQuestion.current)}
        onReact={handleReact}
        threads={threads}
        onOpenReport={(th) => setOpenThread(th)}
      />
      {openThread && (
        <ReportPanel
          thread={threads[openThread.id] || openThread}
          sources={messages.filter((m) => m.speaker?.thread?.id === openThread.id).flatMap((m) => m.sources || [])}
          instanceId={contextRef.current?.instanceId}
          onClose={() => setOpenThread(null)}
        />
      )}
      <MessageInput onSendMessage={handleSendMessage} disabled={isLoading || !historyLoaded} />

      {showClearConfirm && (
        <ModalWrapper visible={showClearConfirm} onClose={() => setShowClearConfirm(false)} showCloseButton={false} zIndex={200}>
          <ConfirmCard>
            <ConfirmText>{t('chatAgent.clearConfirm')}</ConfirmText>
            <ConfirmButtons>
              <CancelBtn onClick={() => setShowClearConfirm(false)}>{t('common.cancel')}</CancelBtn>
              <DeleteBtn onClick={handleClearConversation}>{t('chatAgent.clear')}</DeleteBtn>
            </ConfirmButtons>
          </ConfirmCard>
        </ModalWrapper>
      )}
    </PanelContainer>
  );
};

/** On reload: an assistant message answers for the Kuutio-agents that spoke just before it in the same turn. */
function withSpeakers(messages) {
  let turn = [];
  return messages.map((m) => {
    if (m.type === 'user') { turn = []; return m; }
    if (m.type === 'kuutio_agent') { turn = [...turn, m.speaker]; return m; }
    return turn.length ? { ...m, speakers: turn } : m;
  });
}

function toolLabel(toolName, t) {
  if (toolName === 'headai_get_playbook') {
    return t('chatAgent.tools.headaiPlaybook');
  }
  if (toolName.startsWith('headai_')) {
    return t('chatAgent.tools.headaiAnalysis');
  }
  const labels = {
    search_research: t('chatAgent.tools.searchResearch'),
    get_workshop_data: t('chatAgent.tools.workshopData'),
    get_solutions: t('chatAgent.tools.solutions'),
    get_pivot_points: t('chatAgent.tools.pivotPoints'),
    get_lens_analysis: t('chatAgent.tools.lensAnalysis'),
    search_workshop_nodes: t('chatAgent.tools.workshopNodes'),
    get_impact_path: t('chatAgent.tools.impactPath'),
    get_organisations: t('chatAgent.tools.organisations'),
    list_agents: t('chatAgent.tools.listAgents'),
    ask_kuutio: t('chatAgent.tools.askKuutio'),
    bounce: t('chatAgent.tools.bounce'),
    deep_dive: t('chatAgent.tools.deepDive'),
  };
  return labels[toolName] || t('chatAgent.tools.fallback');
}

export default ChatPanel;
